import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Edit, BookOpen, UserCheck, User,
    FileText, Clock, AlertTriangle, CheckCircle2, X, Building
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import trainingService from '../../../api/services/trainingService';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { formatDate } from '../../../utils/stringUtils';

// Constants
const assigneeTypes = [
    { value: 'individual', label: 'Individual Users', icon: User },
    { value: 'department', label: 'Departments', icon: Building }
];

const courseStatusMeta = {
    assigned: { label: 'Assigned', color: 'text-blue-600 bg-blue-100' },
    in_progress: { label: 'In Progress', color: 'text-yellow-600 bg-yellow-100' },
    completed: { label: 'Completed', color: 'text-green-600 bg-green-100' },
    overdue: { label: 'Overdue', color: 'text-red-600 bg-red-100' }
};

// Component
const AssignmentDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Helpers
    const capitalizeValue = (value) => {
        if (!value) return '';
        const specialCases = {
            'soft_skills': 'Soft Skills',
            'all_employees': 'All Employees',
            'super_admin': 'Super Admin',
            'hr_manager': 'HR Manager',
            'hr_executive': 'HR Executive',
            'sales_manager': 'Sales Manager',
            'sales_executive': 'Sales Executive',
            'business_relationship_manager': 'Business Relationship Manager',
            'business_development_executive': 'Business Development Executive',
            'dispatch_manager': 'Dispatch Manager',
            'support_manager': 'Support Manager'
        };
        if (specialCases[value]) return specialCases[value];
        return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
    };

    const formatDuration = (minutes) => {
        if (!minutes || minutes === 0) return '0m';
        const totalSeconds = Math.round(minutes * 60);
        const hours = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (mins > 0) parts.push(`${mins}m`);
        if (secs > 0 && hours === 0) parts.push(`${secs}s`);
        return parts.length > 0 ? parts.join(' ') : '0m';
    };

    const clampPercentage = (value) => Math.min(100, Math.max(0, Math.round(value)));

    const getQuizAssigneeStatusClasses = (status) => {
        switch ((status || 'assigned').toLowerCase()) {
            case 'passed': return 'bg-green-100 text-green-700';
            case 'failed': return 'bg-red-100 text-red-700';
            case 'overdue': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const calculateAssigneeProgress = (assignee) => {
        if (!assignee) return 0;
        const completedLessons = typeof assignee.progress?.completedLessons === 'number' ? assignee.progress.completedLessons : null;
        const totalLessons = typeof assignee.progress?.totalLessons === 'number' ? assignee.progress.totalLessons : null;
        if (completedLessons !== null && totalLessons !== null && totalLessons > 0) {
            const progress = (Math.min(completedLessons, totalLessons) / totalLessons) * 100;
            return clampPercentage(progress);
        }
        if (typeof assignee.progress?.percentage === 'number') return clampPercentage(assignee.progress.percentage);
        if (typeof assignee.progress === 'number') return clampPercentage(assignee.progress);
        return 0;
    };

    const calculateAssigneeStatus = (assignee, assignmentDueDate) => {
        if (!assignee) return 'assigned';
        const progressValue = calculateAssigneeProgress(assignee);
        const progressStatus = assignee.progress?.status;
        const baseStatus = assignee.status;
        const isCompletedStatus = ['completed', 'passed', 'failed'].includes(progressStatus);
        const hasCompleted = progressValue >= 100 || baseStatus === 'completed' || baseStatus === 'passed' || baseStatus === 'failed' || isCompletedStatus;
        if (hasCompleted) return 'completed';
        const dueDate = assignmentDueDate ? new Date(assignmentDueDate) : null;
        const today = new Date();
        const isOverdue = dueDate && dueDate < today;
        if (isOverdue) return 'overdue';
        const hasProgress = progressValue > 0 || baseStatus === 'in_progress' || ['in_progress', 'started'].includes(progressStatus);
        if (hasProgress) return 'in_progress';
        return 'assigned';
    };

    const getAssignmentContentType = (assignment) => (assignment?.contentType || 'course');
    const isQuizAssignment = (assignment) => getAssignmentContentType(assignment) === 'quiz';
    const getAssignmentContent = (assignment) => (isQuizAssignment(assignment) ? assignment.quiz : assignment.course);
    const getAssignmentTitle = (assignment) => {
        const content = getAssignmentContent(assignment);
        if (!content) return isQuizAssignment(assignment) ? 'Untitled Quiz' : 'Untitled Course';
        return content.title || (isQuizAssignment(assignment) ? 'Untitled Quiz' : 'Untitled Course');
    };
    const getAssignmentDurationLabel = (assignment) => {
        if (isQuizAssignment(assignment)) {
            const timeLimit = assignment.quiz?.timeLimit;
            return timeLimit ? `${timeLimit} min` : 'N/A';
        }
        const duration = assignment.course?.duration;
        return duration ? formatDuration(Number(duration)) : 'N/A';
    };
    const getAssignmentIdentifier = (assignment) => {
        const content = getAssignmentContent(assignment);
        if (isQuizAssignment(assignment)) return content?.quizId || content?._id || 'N/A';
        return content?.courseId || 'N/A';
    };

    // Queries
    const { data: assignment, isLoading, error } = useQuery({
        queryKey: ['training_assignment', id],
        queryFn: async () => {
            const result = await trainingService.getAssignmentById(id);
            if (!result.success) throw new Error(result.error);
            return result.data;
        }
    });

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
            </div>
        );
    }

    if (error || !assignment) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center p-4">
                <AlertTriangle className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Assignment</h2>
                <p className="text-gray-500 mb-4">{error?.message || 'Assignment not found'}</p>
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 bg-agrosaf-green text-white rounded-lg"
                >
                    Go Back
                </button>
            </div>
        );
    }

    return (
        <div className="mx-auto px-4 sm:px-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-900 group"
                            title="Back to List"
                        >
                            <ArrowLeft className="h-6 w-6 group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Assignment Details</h2>
                            <p className="text-sm text-gray-500">Overview of the assignment configuration and assignee progress</p>
                        </div>
                    </div>
                </div>

                <div>
                    <div className="space-y-6">
                        {/* Assignment Header */}
                        <div className="bg-gradient-to-r from-agrosaf-green to-green-600 rounded-lg p-6 text-white">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-2xl font-bold mb-2 break-words capitalize">{getAssignmentTitle(assignment)}</h3>
                                </div>
                            </div>
                        </div>

                        {/* Content Information */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <BookOpen className="h-5 w-5 mr-2 text-agrosaf-green" />
                                {isQuizAssignment(assignment) ? 'Quiz Information' : 'Course Information'}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Title</label>
                                    <p className="text-base text-gray-900 mt-1 break-words capitalize">{getAssignmentTitle(assignment)}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-500">{isQuizAssignment(assignment) ? 'Quiz ID' : 'Course Code'}</label>
                                    <p className="text-base text-gray-900 mt-1">{getAssignmentIdentifier(assignment)}</p>
                                </div>
                                {!isQuizAssignment(assignment) && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-500">Duration</label>
                                        <p className="text-base text-gray-900 mt-1">{getAssignmentDurationLabel(assignment)}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Due Date</label>
                                    <p className="text-base text-gray-900 mt-1">
                                        {assignment.dueDate ? formatDate(assignment.dueDate, { includeTime: false }) : 'N/A'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Assignee Information */}
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                <UserCheck className="h-5 w-5 mr-2 text-agrosaf-green" />
                                Assignees ({assignment.assignees?.length || 0})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-500">Assignee Type</label>
                                    <p className="text-base text-gray-900 mt-1 capitalize">
                                        {assignment.assigneeType || 'N/A'}
                                    </p>
                                </div>
                            </div>
                            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                                {assignment.assignees && assignment.assignees.length > 0 ? (
                                    assignment.assignees.map((assignee, index) => (
                                        <div key={assignee._id || index} className="border border-gray-200 rounded-lg p-4 hover:border-agrosaf-green transition-colors">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center space-x-3 mb-2">
                                                        <div className="w-10 h-10 bg-agrosaf-green bg-opacity-10 rounded-full flex items-center justify-center">
                                                            <User className="h-5 w-5 text-agrosaf-green" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">
                                                                {assignee.user?.firstName} {assignee.user?.lastName}
                                                            </p>
                                                            <p className="text-sm text-gray-500">{assignee.user?.email}</p>
                                                        </div>
                                                    </div>
                                                    {!isQuizAssignment(assignment) ? (
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                                                            <div>
                                                                <label className="text-gray-500 block">Status</label>
                                                                {(() => {
                                                                    const assigneeStatus = calculateAssigneeStatus(assignee, assignment.dueDate);
                                                                    return (
                                                                        <p className={`mt-1 font-medium capitalize ${assigneeStatus === 'completed' ? 'text-green-600' :
                                                                            assigneeStatus === 'in_progress' ? 'text-yellow-600' :
                                                                                assigneeStatus === 'overdue' ? 'text-red-600' :
                                                                                    'text-blue-600'
                                                                            }`}>
                                                                            {capitalizeValue(assigneeStatus || 'assigned')}
                                                                        </p>
                                                                    );
                                                                })()}
                                                            </div>
                                                            <div>
                                                                <label className="text-gray-500 block">Progress</label>
                                                                {(() => {
                                                                    const assigneeProgress = calculateAssigneeProgress(assignee);
                                                                    return (
                                                                        <div className="flex items-center mt-1">
                                                                            <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                                                                                <div
                                                                                    className="bg-agrosaf-green h-2 rounded-full"
                                                                                    style={{ width: `${assigneeProgress}%` }}
                                                                                ></div>
                                                                            </div>
                                                                            <span className="text-sm font-medium">{assigneeProgress}%</span>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                            <div>
                                                                <label className="text-gray-500 block">Assigned Date</label>
                                                                <p className="mt-1 font-medium">{assignee.assignedDate ? formatDate(assignee.assignedDate, { includeTime: false }) : 'N/A'}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="mt-3 space-y-3 text-sm">
                                                            <div>
                                                                <label className="text-gray-500 block">Status</label>
                                                                <div className="mt-1 flex flex-wrap items-center gap-2">
                                                                    <span
                                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getQuizAssigneeStatusClasses(assignee.status)}`}
                                                                    >
                                                                        {capitalizeValue(assignee.status || 'assigned')}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500">
                                                                        {assignee.quizAttempt?.submittedAt
                                                                            ? `Attempted on ${formatDate(assignee.quizAttempt.submittedAt, { includeTime: true })}`
                                                                            : 'Not attempted yet'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500 text-center py-4">No assignees found</p>
                                )}
                            </div>
                        </div>

                        {/* Notes */}
                        {assignment.notes && (
                            <div className="bg-white border border-gray-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                    <FileText className="h-5 w-5 mr-2 text-agrosaf-green" />
                                    Notes
                                </h3>
                                <p className="text-base text-gray-700 whitespace-pre-wrap break-words">
                                    {assignment.notes}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssignmentDetailsPage;
